// src/AppInitializer.tsx
import { lazy, Suspense, useState, useMemo, useEffect } from 'react';
import { AppLogger } from './utils/loggers';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App'; // Your main application component that will be rendered after init
import { ApiClientProvider } from './providers/ApiClientProvider';
import { createKeycloakInstance } from './keycloak';
import NotificationProvider from './features/notifications/context/NotificationProvider';
import PasswordResetForm from './components/PasswordResetForm';
import LoginForm from './components/LoginForm';
// EdgeDocsCore styles registry wiring
import { setStylesRegistry } from './features/dms/EdgeDocs/EdgeDocsCore/styles/registry';
import { DEFAULT_FIRM_STYLES } from './features/dms/EdgeDocs/EdgeDocsCore/styles/firmStyles';
// KeycloakInstance type import kept as a stub for future use
import type { KeycloakInstance } from 'keycloak-js';
import { getDesktopKeycloakClient, isDesktopRuntime } from './auth/desktopAuth';
import {
  clearStoredWebSession,
  isRefreshTokenUsable,
  postLoginUrl,
  readStoredWebSession,
  stripFailedOidcCallback,
} from './auth/webSession';
import UpdateCenterPage from './features/desktop/UpdateCenterPage';

const GuestJoinPage = lazy(() => import('./features/meeting/GuestJoinPage'));
const KnockJoinPage = lazy(() => import('./features/meeting/KnockJoinPage'));
const MasterOperatorPortal = lazy(() => import('./features/admin/routes/MasterOperatorPortal'));
const IntakeVaultPage = lazy(() => import('./features/intake/components/IntakeVaultPage'));
const PublicClientPaymentPage = lazy(() => import('./features/billing/components/PublicClientPaymentPage'));
const PublicFormPage = lazy(() => import('./features/intake/components/PublicFormPage'));


const FIRM_ID_STORAGE_KEY = 'edgelexFirmId';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

const tokenLogger = (tokens: unknown) => {
  const typedTokens = tokens as { token?: string; refreshToken?: string };
  AppLogger.auth.keycloakTokens(!!typedTokens?.token, { tokens: typedTokens });
  if (typedTokens?.token) {
    const parsed = JSON.parse(atob(typedTokens.token.split('.')[1]));
    AppLogger.auth.tokenParsed(parsed);
    (window as unknown as Record<string, unknown>).edgelexTokenParsed = parsed;

    // Sync refreshed tokens back to localStorage so the API client always
    // picks up the latest token (fixes stale-token 401s after silent refresh)
    if (!isDesktopRuntime()) localStorage.setItem('edgelex_access_token', typedTokens.token);
    if (typedTokens.refreshToken && !isDesktopRuntime()) {
      localStorage.setItem('edgelex_refresh_token', typedTokens.refreshToken);
    }
  }
};

// Login Handler Component - handles /login?realm=X URLs
function LoginHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    const realmParam = searchParams.get('realm');
    
    if (realmParam) {
      AppLogger.auth.keycloakInit(realmParam, { source: 'LoginHandler', autoSetFromUrl: true });
      
      // Store the firm ID
      localStorage.setItem(FIRM_ID_STORAGE_KEY, realmParam);
      
      // Redirect to root - AppInitializerInner will pick up the stored firm ID
      // and automatically start the authentication flow
      navigate('/', { replace: true });
    } else {
      // No realm parameter, redirect to normal firm selection
      AppLogger.auth.keycloakInit('none', { source: 'LoginHandler', noRealmParam: true, redirectingToFirmSelection: true });
      navigate('/', { replace: true });
    }
  }, [searchParams, navigate]);
  
  // Show loading while processing
  return (
    <div className="min-h-screen bg-gray-800 text-gray-100 flex flex-col items-center justify-center">
      <div className="bg-gray-900 p-8 rounded-xl shadow-2xl">
        <div className="flex justify-center mb-6">
          <svg className="w-12 h-12 text-blue-500 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p className="text-xl text-center">Setting up your firm workspace...</p>
        <p className="text-sm text-gray-400 text-center mt-2">This will only take a moment</p>
      </div>
    </div>
  );
}

// Enhanced LoginForm Component - handles EdgeLex login with success callback
function EnhancedLoginForm() {
  const handleLoginSuccess = async (firmId: string, accessToken: string, refreshToken: string) => {
    try {
      AppLogger.auth.success({ firmId, source: 'EnhancedLoginForm' });
      
      // Store the firm ID and tokens
      localStorage.setItem(FIRM_ID_STORAGE_KEY, firmId);
      localStorage.setItem('edgelex_access_token', accessToken);
      localStorage.setItem('edgelex_refresh_token', refreshToken);
      
      // Reload on a clean address. A leftover Keycloak callback fragment
      // (#error=login_required&state=…) would otherwise survive the reload and
      // make keycloak-js ignore the tokens just stored.
      window.location.replace(postLoginUrl());
      
    } catch (error) {
      AppLogger.auth.keycloakEvent('loginError', error, { source: 'EnhancedLoginForm' });
    }
  };

  const handleLoginError = (errorMessage: string) => {
    AppLogger.auth.keycloakEvent('loginError', errorMessage, { source: 'EnhancedLoginForm' });
  };

  // Add a reset button to clear stored data if there are issues
  const handleReset = () => {
    AppLogger.auth.keycloakEvent('reset', undefined, { source: 'EnhancedLoginForm', clearingStoredData: true });
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/?reset=true';
  };

  // Use the same beautiful LoginForm component that works at /Login
  return (
    <div>
      <LoginForm 
        onLoginSuccess={handleLoginSuccess}
        onLoginError={handleLoginError}
      />
      {/* Add a small reset link for troubleshooting */}
      <div className="text-center mt-4">
        <button
          onClick={handleReset}
          className="text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Clear stored data and reset
        </button>
      </div>
    </div>
  );
}

// Inner component that needs access to router hooks
function ExternalSignupRedirect() {
  useEffect(() => { window.location.replace('https://edgelex.com/signup/'); }, []);
  return null;
}

function AppInitializerInner() {
  const [searchParams] = useSearchParams();
  // Wire EdgeDocsCore styles registry at startup
  useEffect(() => {
    try { setStylesRegistry(DEFAULT_FIRM_STYLES); } catch {}
  }, []);
  
  // Check for force reset parameter
  useEffect(() => {
    if (searchParams.get('reset') === 'true') {
      AppLogger.auth.keycloakEvent('forceReset', undefined, { source: 'AppInitializer', clearingAllData: true });
      localStorage.clear();
      sessionStorage.clear();
      // Remove the reset parameter and reload
      window.history.replaceState({}, '', window.location.pathname);
      window.location.reload();
      return;
    }
  }, [searchParams]);

  const [firmId] = useState<string | null>(() => {
    const storedFirmId = isDesktopRuntime() ? 'corp' : localStorage.getItem(FIRM_ID_STORAGE_KEY);
    // Return stored firm ID or null - no special development defaults
    return storedFirmId;
  });

  const [initError, setInitError] = useState<string | null>(null);

  // Decide once per page load whether the stored sign-in can still work.
  // A refresh token past the realm's 30-minute session idle limit is dead:
  // handing it to keycloak-js would only bounce the browser through the
  // Keycloak login page before landing on the same form we can show now.
  // Any failed-callback leftovers in the address bar are removed first.
  const [storedSessionUsable] = useState<boolean>(() => {
    if (isDesktopRuntime()) return true;
    stripFailedOidcCallback();
    const stored = readStoredWebSession();
    if (!stored) return false;
    if (isRefreshTokenUsable(stored.refreshToken)) return true;
    AppLogger.auth.keycloakEvent('storedSessionExpired', undefined, { source: 'AppInitializer', firmId: stored.firmId });
    clearStoredWebSession();
    return false;
  });
  const hasValidTokens = () => storedSessionUsable;

  const keycloakInstance = useMemo(() => {
    if (firmId && hasValidTokens()) {
      AppLogger.auth.keycloakInit(firmId);
      try {
        const instance = isDesktopRuntime() ? getDesktopKeycloakClient() : createKeycloakInstance(firmId);
        // Expose the Keycloak instance on window so the API client can access
        // the live token via window.keycloak.token (always up-to-date after refresh)
        (window as any).keycloak = instance;
        return instance;
      } catch (error) {
        AppLogger.auth.keycloakEvent('initFailed', error, { firmId });
        setInitError('Failed to initialize authentication system');
        return null;
      }
    }
    AppLogger.auth.keycloakEvent('notReady', undefined, { hasFirmId: !!firmId, hasValidTokens: hasValidTokens() });
    return null;
  }, [firmId]);

  // Handle initialization errors
  if (initError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Initialization Error</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{initError}</p>
          <button
            onClick={() => {
              localStorage.clear();
              sessionStorage.clear();
              window.location.href = '/?reset=true';
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Reset and Try Again
          </button>
        </div>
      </div>
    );
  }

  // If no firm ID or no valid tokens, show the same beautiful login form
  if (!firmId || !hasValidTokens() || !keycloakInstance) {
    return <EnhancedLoginForm />;
  }

  // Firm ID is set and we have tokens, render the ReactKeycloakProvider
  return (
    <QueryClientProvider client={queryClient}>
      <ReactKeycloakProvider
        authClient={keycloakInstance}
        initOptions={{
          // No onLoad. The tokens below come from the custom form's password
          // grant, which never sets a Keycloak browser SSO cookie, so a
          // 'check-sso' round trip could only end at the Keycloak login page.
          // keycloak-js validates the tokens with a refresh; if that fails it
          // signs out (onAuthLogout below) and the custom form is shown.
          pkceMethod: 'S256',
          checkLoginIframe: false,
          // Add token to init options if available
          token: isDesktopRuntime() ? undefined : localStorage.getItem('edgelex_access_token') || undefined,
          refreshToken: isDesktopRuntime() ? undefined : localStorage.getItem('edgelex_refresh_token') || undefined,
          // Prevent automatic redirects to Keycloak login
          enableLogging: true,
          // Add timeout to prevent hanging
          messageReceiveTimeout: 10000,
          // Additional options to prevent redirects
          redirectUri: isDesktopRuntime() ? undefined : window.location.origin,
          checkLoginIframeInterval: 5,
          // CRITICAL: Request proper scopes including openid
          scope: 'openid profile email roles'
        }}
        onEvent={(event, error) => {
          AppLogger.auth.keycloakEvent(event, error);
          
          // Handle authentication failures gracefully
          if (event === 'onAuthError') {
            AppLogger.auth.keycloakEvent('authError', error, { setInitError: true });
            setInitError(`Authentication failed: ${error || 'Unknown error'}`);
          }
          
          if (event === 'onAuthLogout' || event === 'onInitError') {
            AppLogger.auth.keycloakEvent(event === 'onInitError' ? 'initError' : 'logout', error, { clearingTokens: true });
            if (!isDesktopRuntime()) {
              clearStoredWebSession();
              // Back to the custom form on a clean address (never the Keycloak page).
              window.location.replace(postLoginUrl());
            }
          }
          
          // Log successful authentication
          if (event === 'onAuthSuccess') {
            AppLogger.auth.success();
          }
        }}
        onTokens={tokenLogger}
        LoadingComponent={
          <div className="min-h-screen bg-gray-800 text-gray-100 flex flex-col items-center justify-center">
            <p className="text-xl">Initializing AI Workspace for {firmId}...</p>
            <p className="text-sm text-gray-400 mt-2">Connecting to authentication system...</p>
          </div>
        }
      >
        <ApiClientProvider>
          <NotificationProvider>
            <App />
          </NotificationProvider>
        </ApiClientProvider>
      </ReactKeycloakProvider>
    </QueryClientProvider>
  );
}

// Manual Reset Component - bypasses all authentication
function ManualResetPage() {
  const handleCompleteReset = () => {
    // Clear everything possible
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear IndexedDB if it exists
    if ('indexedDB' in window) {
      indexedDB.databases().then(databases => {
        databases.forEach(db => {
          if (db.name) indexedDB.deleteDatabase(db.name);
        });
      });
    }
    
    // Clear service worker cache if it exists
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
    
    // Force reload to root without any parameters
    window.location.href = window.location.origin;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="max-w-md mx-auto text-center p-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Complete System Reset</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          This will clear all cached data and reset your EdgeLex session completely.
        </p>
        <button
          onClick={handleCompleteReset}
          className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors mb-4"
        >
          Reset Everything and Start Fresh
        </button>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          After clicking, you'll be redirected to a clean login page.
        </p>
      </div>
    </div>
  );
}

// Main AppInitializer component that wraps everything in Router
function AppInitializer() {
  // PUBLIC opaque payment request. The token identifies only this request;
  // payment credentials are collected on Stripe-hosted Checkout.
  const clientPaymentMatch = window.location.pathname.match(/^\/secure\/payment\/([A-Za-z0-9_-]{40,128})$/);
  if (clientPaymentMatch) {
    return <PublicClientPaymentPage token={clientPaymentMatch[1]} />;
  }
  // PUBLIC verified-email Intake Vault. The invitation is only a bootstrap
  // credential; an HttpOnly session is required before any document is shown.
  const intakeVaultMatch = window.location.pathname.match(/^\/secure\/intake\/([A-Za-z0-9_-]{40,128})$/);
  if (intakeVaultMatch) {
    return <IntakeVaultPage token={intakeVaultMatch[1]} />;
  }
  // F.1 PUBLIC surface: guest meeting links bypass firm selection + Keycloak
  // entirely — the opaque token is the sole credential, honored only by the
  // guest endpoints. Everything else in the SPA remains login-gated.
  const guestJoinMatch = window.location.pathname.match(/^\/meet\/join\/([A-Za-z0-9_-]{20,})$/);
  if (guestJoinMatch) {
    return <GuestJoinPage token={guestJoinMatch[1]} />;
  }
  // Accountable open link: PUBLIC knock page — identify first, host admits,
  // admission mints a personal guest token (no anonymous joining exists).
  const knockMatch = window.location.pathname.match(/^\/meet\/knock\/([a-f0-9]{48})$/);
  if (knockMatch) {
    return <KnockJoinPage token={knockMatch[1]} />;
  }

  return (
    <Router>
      <Routes>
        {/* Manual reset route - completely bypasses all authentication */}
        <Route path="/reset" element={<ManualResetPage />} />

        {/* Installed-desktop update control remains available before login. */}
        <Route path="/update" element={<UpdateCenterPage />} />
        
        {/* Signup route - public access */}
        {/* Signup lives on the marketing site now (nginx also 301s this path). */}
        <Route path="/signup" element={<ExternalSignupRedirect />} />
        
        {/* Password reset route - public access */}
        <Route path="/PasswordReset" element={<PasswordResetForm />} />
        
        {/* Custom login form - redirect to root for consistent experience */}
        <Route path="/Login" element={<Navigate to="/" replace />} />
        
        {/* Login handler - processes /login?realm=X URLs */}
        <Route path="/login" element={<LoginHandler />} />

        {/* Master Operator Portal - superadmin access using Keycloak master realm */}
        <Route path="/master" element={<MasterOperatorPortal />} />

        {/* PUBLIC intake form (/form/<form id>): a prospect's page, embedded by firm websites.
            It never touches firm selection or Keycloak; the form id is the only selector and the
            backend re-authorizes it on every call. Placed here, before AppInitializerInner, because
            that component draws the EdgeLex sign-in for any unauthenticated visitor. */}
        <Route path="/form/:formId" element={<PublicFormPage />} />

        {/* All other routes go through the normal app initialization */}
        <Route path="*" element={<AppInitializerInner />} />
      </Routes>
    </Router>
  );
}

export default function AppInitializerWithSuspense() {
  return <Suspense fallback={<div role="status" className="flex min-h-screen items-center justify-center bg-[var(--lex-surface-app)] text-[var(--lex-text-secondary)]">Loading workspace…</div>}><AppInitializer /></Suspense>;
}
